import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, { addEdge, Background, Controls, applyNodeChanges, applyEdgeChanges, Handle, Position } from 'reactflow';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import dagre from 'dagre';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'reactflow/dist/style.css';

// ==========================================
// CLOUD-WEICHE: Erkennt ob wir lokal oder im Internet sind
// ==========================================
const API_BASE_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:8000'
    : 'https://api.mykliniktools.de';

// ==========================================
// Der interaktive Custom Node
// ==========================================
const CustomNode = ({ data }) => {
    const [hoveredBar, setHoveredBar] = useState(null);
    const cleanLabel = (name) => name.replace(/^[0-9]+:\s*/, '');

    return (
        <div style={{
            background: data.type === 'scalar' ? '#e1f5fe' : '#fff3e0',
            border: data.isTarget ? '3px solid #FFD700' : '1px solid #222',
            boxShadow: data.isTarget ? '0 0 10px rgba(255, 215, 0, 0.6)' : 'none',
            borderRadius: '6px', padding: '10px', width: '210px', fontFamily: 'sans-serif', position: 'relative'
        }}>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                {data.label}
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '0 0 10px 0' }}/>

            <div
                style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', height: '60px', gap: '3px', width: '100%' }}
                onMouseLeave={() => setHoveredBar(null)}
            >
                <div style={{ position: 'absolute', bottom: '25%', width: '100%', borderBottom: '1px dashed rgba(0,0,0,0.1)', zIndex: 0 }}></div>
                <div style={{ position: 'absolute', bottom: '50%', width: '100%', borderBottom: '1px dashed rgba(0,0,0,0.15)', zIndex: 0 }}></div>
                <div style={{ position: 'absolute', bottom: '75%', width: '100%', borderBottom: '1px dashed rgba(0,0,0,0.1)', zIndex: 0 }}></div>

                {data.distribution && data.distribution.map((d, i) => {
                    const heightPct = Math.max(d.value * 100, 2);
                    const isEvident = data.currentEvidence === d.name;
                    const hasAnyEvidence = !!data.currentEvidence;

                    let bgColor = data.type === 'scalar' ? '#29b6f6' : '#ffb74d';
                    if (hasAnyEvidence && !isEvident) bgColor = '#e0e0e0';
                    if (isEvident) bgColor = data.type === 'scalar' ? '#01579b' : '#e65100';

                    return (
                        <div
                            key={i}
                            onMouseEnter={() => setHoveredBar({ name: cleanLabel(d.name), value: d.value })}
                            onClick={() => data.onEvidenceClick && data.onEvidenceClick(d.name)}
                            style={{
                                flex: 1, background: bgColor, height: `${heightPct}%`,
                                borderTopLeftRadius: '3px', borderTopRightRadius: '3px',
                                transition: 'all 0.3s ease-in-out', cursor: 'pointer',
                                border: isEvident ? '1px solid #000' : 'none', zIndex: 1
                            }}
                        ></div>
                    );
                })}

                {hoveredBar && (
                    <div style={{
                        position: 'absolute', top: '-30px', left: '50%', transform: 'translateX(-50%)',
                        background: '#333', color: '#fff', padding: '4px 8px', borderRadius: '4px',
                        fontSize: '11px', whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)', fontWeight: 'bold'
                    }}>
                        {hoveredBar.name} ➔ {(hoveredBar.value * 100).toFixed(1)}%
                        <div style={{ position: 'absolute', bottom: '-4px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid #333' }}></div>
                    </div>
                )}
            </div>

            <div style={{ fontSize: '10px', color: '#777', textAlign: 'center', marginTop: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                {data.currentEvidence ? (
                    <><b style={{color: '#d32f2f'}}>Evidenz aktiv</b>
                        <button onClick={(e) => { e.stopPropagation(); data.onClearEvidence && data.onClearEvidence(); }} style={{ background: '#ffcdd2', border: 'none', borderRadius: '3px', color: '#c62828', cursor: 'pointer', padding: '2px 5px', fontSize: '9px', fontWeight: 'bold' }}>✖ Reset</button>
                    </>
                ) : 'Wahrscheinlichkeit'}
            </div>
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
        </div>
    );
};

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));
const getLayoutedElements = (nodes, edges) => {
    dagreGraph.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 40 });
    nodes.forEach((n) => dagreGraph.setNode(n.id, { width: 220, height: 140 }));
    edges.forEach((e) => dagreGraph.setEdge(e.source, e.target));
    dagre.layout(dagreGraph);
    return { nodes: nodes.map((n) => ({ ...n, position: { x: dagreGraph.node(n.id).x - 110, y: dagreGraph.node(n.id).y - 70 }})), edges };
};

// ==========================================
// HAUPT-APP (Umbenannt in BayesApp für sauberes Routing)
// ==========================================
const BayesApp = () => {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [evidence, setEvidence] = useState({});
    const [rocData, setRocData] = useState(null);

    const [activeTab, setActiveTab] = useState('network');
    const [dataPreview, setDataPreview] = useState([]);
    const [dataAnalysis, setDataAnalysis] = useState(null);
    const [targetVariable, setTargetVariable] = useState(null);
    const [testSize, setTestSize] = useState(0.3);

    const [isExporting, setIsExporting] = useState(false);
    const [algorithm, setAlgorithm] = useState('HC');

    const toggleEvidence = useCallback((nodeId, categoryName) => {
        setEvidence(prev => {
            const newEv = { ...prev };
            if (newEv[nodeId] === categoryName) delete newEv[nodeId];
            else newEv[nodeId] = categoryName;
            return newEv;
        });
    }, []);

    const clearNodeEvidence = useCallback((nodeId) => {
        setEvidence(prev => { const newEv = { ...prev }; delete newEv[nodeId]; return newEv; });
    }, []);

    const clearAllEvidence = useCallback(() => setEvidence({}), []);

    const nodesWithEvidence = useMemo(() => {
        return nodes.map(n => ({
            ...n, data: { ...n.data, currentEvidence: evidence[n.id], onEvidenceClick: (cat) => toggleEvidence(n.id, cat), onClearEvidence: () => clearNodeEvidence(n.id) }
        }));
    }, [nodes, evidence, toggleEvidence, clearNodeEvidence]);

    const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);

    const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
    const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);
    const onConnect = useCallback((params) => setEdges((eds) => addEdge({ ...params, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#333' }, style: { strokeWidth: 2, stroke: '#333' } }, eds)), []);
    const onEdgeDoubleClick = useCallback((ev, edge) => setEdges((eds) => eds.filter((e) => e.id !== edge.id)), []);

    const onNodeContextMenu = useCallback((event, clickedNode) => {
        event.preventDefault();
        setTargetVariable(clickedNode.id);
        setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, isTarget: n.id === clickedNode.id } })));
    }, []);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await axios.post(`${API_BASE_URL}/upload`, formData);
            const newNodes = response.data.nodes.map((node, index) => ({
                id: node.id, type: 'custom', data: node.data,
                position: { x: (index % 5) * 260, y: Math.floor(index / 5) * 160 + 50 },
            }));
            setNodes(newNodes);
            setDataPreview(response.data.data_preview || []);
            setDataAnalysis(response.data.analysis || null);
            setActiveTab('network');
            setTargetVariable(null);
            setRocData(null);
            setEdges([]);
            setEvidence({});
        } catch (error) { alert("Fehler beim Hochladen."); }
    };

    const handleOptimizeStructure = async () => {
        if (!targetVariable) return alert("Zielvariable (Rechtsklick) fehlt!");
        try {
            const response = await axios.post(`${API_BASE_URL}/optimize-structure`, {
                target: targetVariable,
                algorithm: algorithm
            });
            if (response.data.error) return alert("Fehler: " + response.data.error);
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, response.data.edges);
            setNodes([...layoutedNodes]); setEdges([...layoutedEdges]);
        } catch (error) { alert("Verbindungsfehler."); }
    };

    useEffect(() => {
        if (edges.length === 0) return;
        const runInference = async () => {
            try {
                const res = await axios.post(`${API_BASE_URL}/infer`, { edges, evidence });
                if (res.data.distributions) {
                    setNodes(nds => nds.map(n => {
                        if (res.data.distributions[n.id]) return { ...n, data: { ...n.data, distribution: res.data.distributions[n.id] }};
                        return n;
                    }));
                }
            } catch (e) { console.error("Inferenzfehler", e); }
        };
        runInference();
    }, [edges, evidence]);

    const updateModel = async () => {
        if (!targetVariable) return alert("Zielvariable fehlt!");
        setRocData({ loading: true });
        try {
            const response = await axios.post(`${API_BASE_URL}/calculate-roc`, { edges, target: targetVariable, test_size: testSize });
            setRocData(response.data);
        } catch (error) { setRocData({ error: "Fehler." }); }
    };

    const exportPDF = async () => {
        const reportElement = document.getElementById('report-container');
        if (!reportElement) return;

        setIsExporting(true);

        try {
            const canvas = await html2canvas(reportElement, { scale: 2, backgroundColor: '#ffffff' });
            const imgData = canvas.toDataURL('image/png');

            const pdf = new jsPDF('l', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

            const fileName = targetVariable ? `Bayes-Report-${targetVariable}.pdf` : `Bayes-Netz-Report.pdf`;
            pdf.save(fileName);

        } catch (err) {
            console.error("Fehler beim PDF Export:", err);
            alert("Fehler beim Erstellen des PDFs.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        // HIER WAR DER FEHLER: width war auf 100vw und height auf 100vh. Jetzt ist beides 100%.
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'sans-serif' }}>

            {/* TOP BAR */}
            <div style={{ height: '60px', borderBottom: '1px solid #ccc', padding: '0 20px', display: 'flex', gap: '20px', alignItems: 'center', background: '#f8f9fa' }}>
                <b style={{color: '#333', fontSize: '18px'}}>Klinisches Bayes-Netz</b>
                <input type="file" onChange={handleFileUpload} accept=".csv,.xlsx" style={{ marginLeft: '20px' }} />

                <button onClick={updateModel} style={{ padding: '8px 15px', cursor: 'pointer', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold' }}>
                    Modell-Inferenz & ROC
                </button>

                <button
                    onClick={exportPDF}
                    disabled={isExporting || nodes.length === 0}
                    style={{
                        padding: '8px 15px', cursor: (isExporting || nodes.length === 0) ? 'not-allowed' : 'pointer',
                        background: (isExporting || nodes.length === 0) ? '#ccc' : '#E65100',
                        color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', marginLeft: 'auto'
                    }}
                >
                    {isExporting ? '📸 Erstelle PDF...' : '📄 PDF Report'}
                </button>
            </div>

            <div style={{ background: '#e9ecef', display: 'flex', borderBottom: '1px solid #ccc' }}>
                <button onClick={() => setActiveTab('network')} style={{ padding: '12px 24px', border: 'none', background: activeTab === 'network' ? '#fff' : 'transparent', fontWeight: activeTab === 'network' ? 'bold' : 'normal' }}>🕸️ Netzwerk-Editor</button>
                <button onClick={() => setActiveTab('data')} style={{ padding: '12px 24px', border: 'none', background: activeTab === 'data' ? '#fff' : 'transparent', fontWeight: activeTab === 'data' ? 'bold' : 'normal' }}>📊 Daten-Vorschau</button>
            </div>

            {activeTab === 'network' && (
                <div style={{ background: '#e3f2fd', padding: '10px 20px', borderBottom: '1px solid #90caf9', color: '#0d47a1', fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}>
                    <span><b>Interaktion:</b> Fahre über die Balken, um exakte Werte zu sehen. Klicke zum Setzen von Evidenz!</span>
                    <span>🎯 <b>Rechtsklick</b> markiert die Zielvariable.</span>
                </div>
            )}

            {activeTab === 'network' ? (
                <div id="report-container" style={{ flex: 1, display: 'flex', background: '#ffffff', minHeight: 0 }}>
                    <div style={{ flex: 3, position: 'relative' }}>
                        <ReactFlow nodes={nodesWithEvidence} edges={edges} nodeTypes={nodeTypes} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onNodeContextMenu={onNodeContextMenu} onEdgeDoubleClick={onEdgeDoubleClick} fitView>
                            <Background color="#aaa" gap={20} />
                            <Controls />
                        </ReactFlow>
                    </div>

                    <div style={{ flex: 1, borderLeft: '1px solid #ccc', padding: '20px', background: '#fff', minWidth: '350px', maxWidth: '400px', overflowY: 'auto' }}>
                        <h3>Modell-Parameter</h3>

                        {targetVariable && (
                            <div style={{ padding: '10px', background: '#fff9c4', border: '1px solid #fbc02d', borderRadius: '4px', marginBottom: '15px' }}>
                                <div style={{marginBottom: '10px'}}><b>Zielvariable:</b> {targetVariable}</div>

                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <select
                                        value={algorithm}
                                        onChange={(e) => setAlgorithm(e.target.value)}
                                        style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                                    >
                                        <option value="HC">Hill-Climb (Tabu Search)</option>
                                        <option value="PC">PC-Algorithmus (Kausalität)</option>
                                        <option value="Tree">Chow-Liu (Info-Theorie)</option>
                                    </select>
                                </div>

                                <button onClick={handleOptimizeStructure} style={{ width: '100%', padding: '8px', background: '#1976D2', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>✨ Auto-Struktur finden</button>
                            </div>
                        )}

                        <div style={{ padding: '15px', background: '#e8f5e9', border: '1px solid #81c784', borderRadius: '4px', marginBottom: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                <b>Trainingskohorte:</b> <span>{Math.round((1 - testSize) * 100)}%</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <b>Testkohorte:</b> <span>{Math.round(testSize * 100)}%</span>
                            </div>
                            <input
                                type="range"
                                min="0.1"
                                max="0.6"
                                step="0.05"
                                value={testSize}
                                onChange={(e) => setTestSize(parseFloat(e.target.value))}
                                style={{ width: '100%', cursor: 'pointer' }}
                            />
                        </div>

                        {Object.keys(evidence).length > 0 && (
                            <div style={{ padding: '10px', background: '#ffebee', border: '1px solid #ef5350', borderRadius: '4px', marginBottom: '15px', fontSize: '13px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <b>Gesetzte Patienten-Evidenz:</b>
                                    <button onClick={clearAllEvidence} style={{ background: '#c62828', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', padding: '4px 8px', fontSize: '11px' }}>Alle löschen</button>
                                </div>
                                <ul style={{ margin: '0 0 0 20px', padding: 0 }}>
                                    {Object.entries(evidence).map(([k, v]) => <li key={k}>{k}: {v.replace(/^[0-9]+:\s*/, '')}</li>)}
                                </ul>
                            </div>
                        )}
                        <hr />
                        <h3>Modell-Performance</h3>
                        {rocData && rocData.error && <div style={{ padding: '10px', background: '#ffcdd2', color: '#c62828', borderRadius: '4px', marginBottom: '10px' }}><b>Fehler:</b> {rocData.error}</div>}

                        <div style={{ width: '100%', height: '250px', border: '1px dashed #ccc', marginBottom: '20px', background: '#f9f9f9', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {rocData && rocData.loading ? (
                                <p style={{ color: '#666' }}>Berechne Inferenz...</p>
                            ) : rocData && rocData.roc_points && rocData.roc_points.length > 0 ? (
                                <LineChart width={300} height={230} data={rocData.roc_points} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="fpr" type="number" domain={[0, 1]} name="FPR" tick={{fontSize: 12}} />
                                    <YAxis dataKey="tpr" type="number" domain={[0, 1]} name="TPR" tick={{fontSize: 12}} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="fpr" stroke="#ccc" strokeDasharray="5 5" dot={false} isAnimationActive={false} />
                                    <Line type="monotone" dataKey="tpr" stroke="#8884d8" strokeWidth={3} dot={false} />
                                </LineChart>
                            ) : ( <p style={{ color: '#999', fontSize: '14px', textAlign: 'center' }}>Klicke auf Berechnen.</p> )}
                        </div>

                        {rocData && rocData.auc !== undefined && !rocData.error && !rocData.loading && (
                            <div style={{ fontSize: '18px', textAlign: 'center', marginBottom: '20px', color: rocData.auc > 0.5 ? '#2e7d32' : '#d32f2f' }}>
                                <b>AUC-Wert: {rocData.auc.toFixed(3)}</b>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ padding: '20px', overflowY: 'auto', flex: 1, background: '#fff' }}>
                    <h3>Datenanalyse (Auto-Cleaning aktiv)</h3>
                    {dataAnalysis && (
                        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '5px', marginBottom: '20px', border: '1px solid #bbdefb' }}>
                            <p style={{ margin: '0 0 10px 0' }}><b>Gesamtanzahl Zeilen:</b> {dataAnalysis.total_rows}</p>

                            {dataAnalysis.removed_columns > 0 && (
                                <p style={{ margin: '0 0 10px 0', color: '#e65100' }}>
                                    <b>⚠️ Aussortierte Spalten (leer oder 0-Varianz):</b> {dataAnalysis.removed_columns}
                                </p>
                            )}

                            <p style={{ margin: 0 }}><b>Gefundene leere Felder (NAs):</b></p>
                            <ul style={{ margin: '5px 0 0 20px' }}>
                                {Object.entries(dataAnalysis.missing_values).map(([col, count]) => (
                                    count > 0 ? <li key={col} style={{ color: '#d32f2f' }}>{col}: {count} fehlende Werte</li> : null
                                ))}
                            </ul>
                            {Object.values(dataAnalysis.missing_values).every(val => val === 0) && (
                                <p style={{ margin: '5px 0 0 0', color: '#2e7d32' }}>Der Datensatz ist sauber!</p>
                            )}
                        </div>
                    )}

                    <h3>Vorschau (erste 10 Zeilen)</h3>
                    {dataPreview.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                            <tr style={{ background: '#f1f1f1' }}>
                                {Object.keys(dataPreview[0]).map(key => <th key={key} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>{key}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {dataPreview.map((row, i) => (
                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    {Object.values(row).map((val, j) => <td key={j} style={{ border: '1px solid #ddd', padding: '8px' }}>{val !== null && val !== undefined ? val.toString() : 'NaN'}</td>)}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>Lade eine Datei hoch, um die Vorschau zu sehen.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default BayesApp;
